import React, { useState, useRef, useEffect } from 'react';

export default function SearchableTagSelect({ value, onChange, tags = [], placeholder = 'Select tag...', style = {}, darkMode = true }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const theme = darkMode ? {
    bg: '#0f1117', card: '#1a1d29', text: '#e5e7eb', textSecondary: '#9ca3af',
    border: '#374151', accent: '#667eea', hover: '#232837'
  } : {
    bg: '#ffffff', card: '#f9fafb', text: '#111827', textSecondary: '#6b7280',
    border: '#e5e7eb', accent: '#667eea', hover: '#f3f4f6'
  };

  const filtered = search
    ? tags.filter(t => t.toLowerCase().includes(search.toLowerCase()))
    : tags;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative', ...style }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '10px', background: theme.bg,
          border: `1px solid ${open ? theme.accent : theme.border}`,
          borderRadius: '6px', color: value ? theme.text : theme.textSecondary,
          fontSize: '13px', cursor: 'pointer', boxSizing: 'border-box',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || placeholder}
        </span>
        <span style={{ fontSize: '10px', color: theme.textSecondary, flexShrink: 0, marginLeft: '8px' }}>
          {open ? '\u25B2' : '\u25BC'}
        </span>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
          background: theme.card, border: `1px solid ${theme.border}`,
          borderRadius: '6px', marginTop: '4px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          maxHeight: '220px', display: 'flex', flexDirection: 'column', overflow: 'hidden'
        }}>
          <div style={{ padding: '6px' }}>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags..."
              style={{
                width: '100%', padding: '8px 10px', background: theme.bg,
                border: `1px solid ${theme.border}`, borderRadius: '4px',
                color: theme.text, fontSize: '12px', boxSizing: 'border-box', outline: 'none'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 4px 4px' }}>
            {!value && (
              <div
                onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                style={{
                  padding: '7px 10px', borderRadius: '4px', cursor: 'pointer',
                  fontSize: '12px', color: theme.textSecondary, fontStyle: 'italic'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = theme.hover}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {placeholder}
              </div>
            )}
            {value && (
              <div
                onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                style={{
                  padding: '7px 10px', borderRadius: '4px', cursor: 'pointer',
                  fontSize: '12px', color: theme.textSecondary, fontStyle: 'italic'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = theme.hover}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                Clear selection
              </div>
            )}
            {filtered.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: '12px', color: theme.textSecondary }}>
                No tags found
              </div>
            )}
            {filtered.map(tag => (
              <div
                key={tag}
                onClick={() => { onChange(tag); setOpen(false); setSearch(''); }}
                style={{
                  padding: '7px 10px', borderRadius: '4px', cursor: 'pointer',
                  fontSize: '12px', color: tag === value ? theme.accent : theme.text,
                  fontWeight: tag === value ? '600' : '400',
                  background: tag === value ? (darkMode ? 'rgba(102,126,234,0.15)' : 'rgba(102,126,234,0.1)') : 'transparent'
                }}
                onMouseEnter={(e) => { if (tag !== value) e.currentTarget.style.background = theme.hover; }}
                onMouseLeave={(e) => { if (tag !== value) e.currentTarget.style.background = 'transparent'; }}
              >
                {tag}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
